import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { composerEmojiCategories } from '../utils/composerEmojiCatalog';
import styles from './ComposerEmojiPicker.module.css';

const CATEGORY_LABEL_KEYS: Record<string, string> = {
  smileys: 'emojiCategorySmileys',
  gestures: 'emojiCategoryGestures',
  hearts: 'emojiCategoryHearts',
  people: 'emojiCategoryPeople',
  animals: 'emojiCategoryAnimals',
  food: 'emojiCategoryFood',
  travel: 'emojiCategoryTravel',
  activities: 'emojiCategoryActivities',
  objects: 'emojiCategoryObjects',
  symbols: 'emojiCategorySymbols',
  flags: 'emojiCategoryFlags',
};

interface ComposerEmojiPickerProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: (emoji: string) => void;
  buttonClassName: string;
  iconClassName: string;
  disabled?: boolean;
  title?: string;
}

export const ComposerEmojiPicker: React.FC<ComposerEmojiPickerProps> = ({
  isOpen,
  onToggle,
  onClose,
  onPick,
  buttonClassName,
  iconClassName,
  disabled = false,
  title,
}) => {
  const { t } = useTranslation('conversations');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

  const category = composerEmojiCategories[categoryIndex] ?? composerEmojiCategories[0];
  const categoryLabel = useMemo(
    () => t(CATEGORY_LABEL_KEYS[category.key] || category.key),
    [category.key, t],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updatePanelPosition = () => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) {
        return;
      }

      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const viewportPadding = 8;
      const gap = 8;
      const maxLeft = Math.max(viewportPadding, window.innerWidth - panelRect.width - viewportPadding);
      const left = Math.min(Math.max(triggerRect.left, viewportPadding), maxLeft);

      let top = triggerRect.top - panelRect.height - gap;
      if (top < viewportPadding) {
        top = triggerRect.bottom + gap;
      }

      const maxTop = Math.max(viewportPadding, window.innerHeight - panelRect.height - viewportPadding);
      top = Math.min(Math.max(top, viewportPadding), maxTop);
      setPanelPosition({ top, left });
    };

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInTrigger = rootRef.current?.contains(target);
      const isInPanel = panelRef.current?.contains(target);
      if (!isInTrigger && !isInPanel) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div className={styles.wrapper} ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={buttonClassName}
        onClick={onToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-label={title || t('openEmojiPicker')}
        title={title || t('openEmojiPicker')}
      >
        <svg className={iconClassName} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="9" />
          <path d="M9 10h.01M15 10h.01" />
          <path d="M8.4 14.2a4.6 4.6 0 0 0 7.2 0" />
        </svg>
      </button>
      {isOpen && createPortal(
        <div
          ref={panelRef}
          className={styles.panel}
          role="dialog"
          aria-label={t('emojiPicker')}
          style={{ top: panelPosition.top, left: panelPosition.left }}
          onMouseDown={event => event.preventDefault()}
        >
          <div className={styles.categories} role="tablist" aria-label={t('emojiPicker')}>
            {composerEmojiCategories.map((item, index) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={index === categoryIndex}
                className={`${styles.categoryBtn} ${index === categoryIndex ? styles.categoryBtnActive : ''}`}
                onClick={() => setCategoryIndex(index)}
                title={t(CATEGORY_LABEL_KEYS[item.key] || item.key)}
              >
                {item.icon}
              </button>
            ))}
          </div>
          <p className={styles.categoryLabel}>{categoryLabel}</p>
          <div className={styles.grid}>
            {category.emojis.map((emoji, index) => (
              <button
                key={`${category.key}-${index}-${emoji}`}
                type="button"
                className={styles.emojiBtn}
                onClick={() => onPick(emoji)}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ComposerEmojiPicker;
